/**
 * ADR-0016 Phase 5 residual — plan lots in memory, apply remaining deltas via ledger projection.
 *
 * Call sequence:
 * 1. Compute desired `afterLots` (e.g. achat edit sync, delete reverse ops).
 * 2. applyPlannedLotsViaLedger(parsed, liveBeforeLots, afterLots, note, options)
 *    → residual deltas via projection:
 *      - default movementTypePolicy: 'adjustment'
 *      - achat void: 'purchase_void' (−VOID / +PURCHASE)
 *      - rebuild from purchase lines: 'purchase_delta'
 *
 * Do not assign `parsed.lots = afterLots` first (dual-write). Live payload lots are the baseline.
 */

import { sameLotId } from '../../utils/productIdUtils';
import {
  applyInventoryMovements,
  buildLotRemainingDeltaMovements,
  generateCorrelationId
} from './movements';
import {
  assignParsedLotsGuarded,
  runWithInventoryLotProjection
} from './guards/inventoryLotMutationGuard';

function ensureLotsArray(parsed) {
  if (!parsed || typeof parsed !== 'object') return [];
  if (!Array.isArray(parsed.lots)) {
    assignParsedLotsGuarded(
      parsed,
      parsed.lots && typeof parsed.lots === 'object' ? Object.values(parsed.lots) : [],
      { reason: 'lotPlanViaLedger_ensureLotsArray' }
    );
  }
  return parsed.lots;
}

function listLots(raw) {
  if (Array.isArray(raw)) return raw.filter(Boolean);
  if (raw && typeof raw === 'object') return Object.values(raw).filter(Boolean);
  return [];
}

/**
 * After movements projected quantities, overlay non-qty facts from the planned lots
 * and drop rows that the plan removed.
 *
 * @param {object} parsed
 * @param {object[]} afterLots
 */
export function syncLotPlanMetaFromTarget(parsed, afterLots = []) {
  return runWithInventoryLotProjection(() => {
    ensureLotsArray(parsed);
    const targetById = new Map();
    for (const lot of listLots(afterLots)) {
      if (lot?.id == null) continue;
      targetById.set(String(lot.id), lot);
    }

    const next = [];
    const seen = new Set();

    for (const live of parsed.lots || []) {
      if (!live?.id) continue;
      const key = String(live.id);
      const target = targetById.get(key);
      if (!target) {
        // Plan removed this lot — only drop when qty already projected away
        const rem = Number(live.remainingQuantity) || 0;
        if (Math.abs(rem) > 1e-9) {
          // Keep residual (projection should have zeroed); still drop if plan demands
          // but rem left means delta incomplete — keep for safety
          next.push(live);
        }
        continue;
      }
      seen.add(key);
      // Remaining from ledger projection; other fields (total, prices, status) from plan
      next.push({
        ...target,
        remainingQuantity: Number(live.remainingQuantity) || 0,
        id: live.id,
        // Prefer projected remaining for money when plan rem matched; recompute
        totalPurchaseValue:
          (Number(live.remainingQuantity) || 0) *
          (Number(target.purchaseUnitPrice ?? live.purchaseUnitPrice) || 0),
        totalSellingValue:
          (Number(live.remainingQuantity) || 0) *
          (Number(target.sellingUnitPrice ?? live.sellingUnitPrice) || 0)
      });
      const last = next[next.length - 1];
      last.potentialProfit =
        (Number(last.totalSellingValue) || 0) - (Number(last.totalPurchaseValue) || 0);
    }

    // Plan has lots that projection should have created — if missing, materialize shell
    // under projection scope (e.g. zero-qty edge or id mismatch).
    for (const [key, target] of targetById) {
      if (seen.has(key)) continue;
      const rem = Number(target.remainingQuantity) || 0;
      if (Math.abs(rem) < 1e-9 && (Number(target.totalQuantity) || 0) < 1e-9) {
        // Deleted residual — skip insert
        continue;
      }
      next.push({ ...target });
    }

    assignParsedLotsGuarded(parsed, next, { reason: 'syncLotPlanMetaFromTarget' });
    return parsed;
  });
}

/**
 * @param {object} parsed
 * @param {object[]} beforeLots — live lots snapshot before plan
 * @param {object[]} afterLots — planned outcome
 * @param {string} [note]
 * @param {{ businessId?: string, correlationId?: string, skipMetaSync?: boolean, movementTypePolicy?: 'adjustment'|'purchase_void'|'purchase_delta' }} [options]
 * @returns {object[]} added movements
 */
export function applyPlannedLotsViaLedger(
  parsed,
  beforeLots,
  afterLots,
  note = 'lot_plan_sync',
  options = {}
) {
  ensureLotsArray(parsed);
  const correlationId =
    options.correlationId || generateCorrelationId(note || 'lot_plan_sync');
  const movements = buildLotRemainingDeltaMovements(
    beforeLots,
    afterLots,
    note,
    {
      ...options,
      correlationId,
      movementTypePolicy: options.movementTypePolicy || 'adjustment'
    }
  );

  // Ensure planned lot shells exist (id) so ADJUSTMENT can attach when increasing / creating.
  // Zero-qty shells are not a quantity mutation.
  const liveIds = new Set(
    (parsed.lots || []).map((l) => (l?.id != null ? String(l.id) : '')).filter(Boolean)
  );
  for (const lot of listLots(afterLots)) {
    if (lot?.id == null) continue;
    const key = String(lot.id);
    if (liveIds.has(key)) continue;
    // Shell with quantity matching "before" (0) so first PURCHASE/ADJUST +delta projects correctly
    parsed.lots.push({
      ...lot,
      remainingQuantity: 0,
      totalQuantity: 0,
      totalPurchaseValue: 0,
      totalSellingValue: 0,
      potentialProfit: 0
    });
    liveIds.add(key);
  }

  const added = applyInventoryMovements(parsed, movements, {
    businessId: options.businessId,
    defaultCorrelationId: correlationId
  });

  if (options.skipMetaSync !== true) {
    syncLotPlanMetaFromTarget(parsed, afterLots);
  }

  return added;
}

/**
 * True if two lot lists match by id→remaining (for tests / detectors).
 */
export function lotRemainingMapsMatch(a, b, epsilon = 1e-6) {
  const mapOf = (list) => {
    const m = new Map();
    for (const lot of listLots(list)) {
      if (lot?.id == null) continue;
      m.set(String(lot.id), Number(lot.remainingQuantity) || 0);
    }
    return m;
  };
  const ma = mapOf(a);
  const mb = mapOf(b);
  if (ma.size !== mb.size) return false;
  for (const [id, rem] of ma) {
    if (!mb.has(id)) return false;
    if (Math.abs(rem - mb.get(id)) > epsilon) return false;
  }
  return true;
}

export function findLotById(lots, lotId) {
  return listLots(lots).find((l) => sameLotId(l?.id, lotId)) || null;
}
